/**
 * Created by katarinababic on 23.5.25.
 */
import * as React from 'react';
import { useState } from 'react';
import { AlertModal } from '../AlertModal';
import { EventEntry, defaultEventEntry } from '../../Model/Coach/Schedule/EventEntry';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { useSaveEvent, useDeleteEvent, useDeleteAllEvents } from '../../Hooks/useEventEntryMutations';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { Recipe } from '../../Model/Diet/Recipe/Recipe';
import { useRootStore } from '../../Store/useRootStore';
import { useToast } from '../../Hooks/useToast';
import { useRequestNotificationPermission } from '../../Hooks/useRequestNotificationPermission';
import dayjs from 'dayjs';
import { DurationBefore } from '../../Model/Coach/Schedule/NotificationInterval';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '../Button';
import { Theme } from '../../Theme/Theme';
import { ScheduleMenu } from './ScheduleMenu';

export type ScheduleModalProps = {
  exploreEntry: ExploreEntry | Recipe;
  parentEvent?: EventEntry;
  onSchedule?: (event?: EventEntry) => void;
  onDelete?: () => void;
  currentEvent?: EventEntry;
  disableRecurring?: boolean;
};

export const ScheduleModal: React.FC<ScheduleModalProps> = (
  { exploreEntry, parentEvent, currentEvent, onSchedule, onDelete, disableRecurring },
) => {
    const { t } = useTrans();
    const { analytics } = useRootStore();
    const toast = useToast();
    const { requestPermission } = useRequestNotificationPermission();
    const entryName = useLocalized(exploreEntry.name);
    const saveEvent = useSaveEvent();
    const deleteEvent = useDeleteEvent();
    const deleteAllEvents = useDeleteAllEvents();

    const givenEvent = parentEvent ?? currentEvent;
    const buildInitialEvent = React.useCallback(() => {
      if (givenEvent) {
        return defaultEventEntry(givenEvent);
      }

      const customData = {
        name: entryName,
        link: `https://coach.kinastic.com/explore/${exploreEntry.type}/${exploreEntry.id}`,
      };

      return defaultEventEntry({
        objectType: exploreEntry.type,
        objectId: exploreEntry.id,
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        startTime: dayjs().add(1, 'hour').format('HH:mm:ss'),
        endTime: dayjs().add(1, 'hour').format('HH:mm:ss'),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        notificationIntervals: [
          {
            durationBefore: DurationBefore.AT_EVENT,
            customData,
          },
        ],
      } as any);
    }, [entryName, exploreEntry.id, exploreEntry.type, givenEvent]);

    const [tempEvent, setTempEvent] = useState(buildInitialEvent);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleCancel = React.useCallback(() => {
      // close modal
      onSchedule && onSchedule();
    }, [onSchedule]);

    const schedule = React.useCallback(() => {
      if (!tempEvent) return;
      saveEvent
        .mutateAsync(tempEvent)
        .then((newEvent) => {
          toast.showToast(t('explore.scheduler.eventScheduled', { defaultValue: 'Event scheduled' }), 'success');
          onSchedule && onSchedule(newEvent);
          analytics.logEvent('event_scheduled', {
            content_type: newEvent.objectType,
            item_id: newEvent.objectId,
            notificationEnabled: newEvent.notificationEnabled,
            startDate: newEvent.startDate,
            startTime: newEvent.startTime,
            timezone: newEvent.timezone,
            name: entryName.substring(0, 40),
          });
        })
        .then(() => {
          return requestPermission().catch(() => {
            console.log('Notification permission denied');
          });
        })
        .finally(() => {
          // close modal
          onSchedule && onSchedule();
        });
    }, [analytics, entryName, onSchedule, requestPermission, saveEvent, t, tempEvent, toast]);

    const removeAllTitle = t('explore.scheduler.removeAlert.titleSession', {
      value: entryName,
    });
    const removeAllMessage = t('explore.scheduler.removeAlert.messageSession', {
      value: entryName,
    });
    const removeAllText = t('explore.scheduler.removeAlert.removeAllEvents', {
      value: entryName,
    });
    const removeOneText = t('explore.scheduler.removeAlert.removeOnlyThisEvent', {
      defaultValue: 'Remove only this event',
    });

    const unscheduleAll = React.useCallback(() => {
      if (!parentEvent) return;
      deleteAllEvents
        .mutateAsync(parentEvent)
        .then(() => {
          toast.showToast(t('explore.scheduler.eventRemoved', { defaultValue: 'Event removed' }), 'error');
          onDelete && onDelete();
          analytics.logEvent('event_unscheduled', {
            content_type: parentEvent.objectType,
            item_id: parentEvent.objectId,
            aiPlanned: parentEvent.aiPlanned,
            startTime: parentEvent.startTime,
            startDate: parentEvent.startDate,
          });
        })
        .finally(() => {
          // close modal
          onDelete && onDelete();
        });
    }, [analytics, deleteAllEvents, onDelete, parentEvent, t, toast]);

    const unscheduleOne = React.useCallback(() => {
      if (!currentEvent) return;
      deleteEvent
        .mutateAsync(currentEvent)
        .then(() => {
          toast.showToast(t('explore.scheduler.eventRemoved', { defaultValue: 'Event removed' }), 'error');
          if (parentEvent && !parentEvent?.recurringPattern) {
            onDelete && onDelete();
          }
        })
        .finally(() => {
          // close modal
          onDelete && onDelete();
        });
    }, [currentEvent, deleteEvent, onDelete, parentEvent, t, toast]);

    const handleUnschedule = React.useCallback(() => {
      if (parentEvent) {
        setConfirmOpen(true);
      } else {
        unscheduleOne();
      }
    }, [parentEvent, unscheduleOne]);

    return (
      <React.Fragment>
        <Modal isOpen centered size="lg" toggle={handleCancel}>
          <ModalHeader toggle={handleCancel} />
          <ModalBody>
            <ScheduleMenu event={tempEvent} onChange={setTempEvent} disableRecurring={disableRecurring} />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleUnschedule} style={{ color: Theme.Colors.error }}>
              {t('globals.remove', { defaultValue: 'Remove' })}
            </Button>
            <Button color="secondary" onClick={handleCancel}>
              {t('globals.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button color="primary" onClick={schedule}>
              {t('globals.save', { defaultValue: 'Save' })}
            </Button>
          </ModalFooter>
        </Modal>
        <AlertModal
          isOpen={confirmOpen}
          title={removeAllTitle}
          body={removeAllMessage}
          confirmText={removeAllText}
          cancelText={removeOneText}
          onConfirm={() => {
            setConfirmOpen(false);
            unscheduleAll();
          }}
          onCancel={() => {
            setConfirmOpen(false);
            unscheduleOne();
          }}
        />
      </React.Fragment>
    );
};
